/**
 * Utility per richieste API ottimizzate per Telegram WebApp
 * Fornisce metodi per garantire che le richieste API funzionino correttamente in tutti gli ambienti
 */

import { getToken } from './token-storage';
import { isTelegramWebApp } from './telegram-service';

interface FetchOptions extends Omit<RequestInit, 'headers'> {
  addTimestamp?: boolean;
  forceAbsoluteURL?: boolean;
  headers?: Record<string, string>;
}

/**
 * Funzione per normalizzare gli URL delle richieste API 
 * Garantisce che gli URL siano assoluti in ambiente Telegram o quando forzato
 * @param relativeUrl - URL relativo (es. /api/users/1)
 * @param addTimestamp - Se true, aggiunge un timestamp per evitare caching (default: true)
 * @returns URL normalizzato appropriato per l'ambiente corrente
 */
export function normalizeApiUrl(relativeUrl: string, addTimestamp: boolean = true): string {
  // Pulisce l'URL in caso arrivi già con doppi slash
  const cleanRelativeUrl = relativeUrl.startsWith('/') 
    ? relativeUrl 
    : `/${relativeUrl}`;

  // Aggiunge parametro anti-cache se richiesto  
  const urlWithTimestamp = addTimestamp 
    ? `${cleanRelativeUrl}${cleanRelativeUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`
    : cleanRelativeUrl;

  // In ambiente Telegram, usa sempre URL assoluti
  const isInTelegram = isTelegramWebApp();
  if (isInTelegram) {
    const baseUrl = window.location.origin;
    return `${baseUrl}${urlWithTimestamp}`;
  }

  // Altrimenti ritorna l'URL relativo
  return urlWithTimestamp;
}

/**
 * Wrapper fetch ottimizzato per richieste API in tutti gli ambienti
 * Gestisce automaticamente token di autenticazione, URL assoluti e headers anti-cache
 * @param url - URL relativo dell'API (es. /api/photos)
 * @param options - Opzioni fetch standard con proprietà aggiuntive
 * @returns Promise con la risposta fetch
 */
export async function apiFetch(url: string, options: FetchOptions = {}): Promise<Response> {
  const { 
    addTimestamp = true, 
    forceAbsoluteURL = false,
    headers = {},
    ...restOptions 
  } = options;

  // Determina se usare URL assoluto
  const isInTelegram = isTelegramWebApp();
  const useAbsoluteUrl = isInTelegram || forceAbsoluteURL;

  // Normalizza URL con eventuale timestamp anti-cache
  const normalizedUrl = normalizeApiUrl(url, addTimestamp);

  // Ottieni token autenticazione
  const token = getToken();

  // Prepara headers - creiamo un oggetto vuoto e aggiungiamo le coppie chiave-valore
  const finalHeaders: Record<string, string> = {};

  // Aggiungiamo gli header anti-cache
  finalHeaders['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  finalHeaders['Pragma'] = 'no-cache';

  // Aggiungiamo l'identificatore univoco
  finalHeaders['X-Request-ID'] = Math.random().toString(36).substring(2, 15);

  // Aggiungiamo gli header personalizzati
  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value === 'string') {
        finalHeaders[key] = value;
      }
    }
  }

  // Aggiungi token autenticazione se disponibile
  if (token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Log dettagliato per debugging
  console.log(`📡 Richiesta API: ${normalizedUrl}`, {
    ambiente: isInTelegram ? 'TELEGRAM' : 'BROWSER',
    tokenPresente: !!token,
    useAbsoluteUrl
  });

  // Configurazione fetch per l'ambiente specifico
  return fetch(normalizedUrl, {
    ...restOptions,
    headers: finalHeaders,
    // Importante per gestire cookies quando necessario
    credentials: 'include',
    // In Telegram usa 'cors' per gestire richieste cross-origin
    mode: isInTelegram ? 'cors' : 'same-origin'
  })
  .then(async (response) => {
    // Se la risposta non è ok, lancia un errore con il messaggio dal server
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorData;

      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;

        // Se l'utente è bannato, forza il logout e ricarica
        if (response.status === 403 && errorData.code === 'USER_BANNED') {
          console.log('[API] Utente bannato rilevato, forzando logout...');

          // Clear storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          sessionStorage.clear();

          // Ricarica la pagina per mostrare la schermata bannato
          window.location.reload();
          // Lancia un errore per mantenere il tipo di ritorno corretto
          // (la pagina verrà ricaricata quindi questo non verrà mai visto)
          throw new Error('USER_BANNED');
        }
      } catch (e) {
        // Se non riusciamo a parsare il JSON, usa il messaggio di stato HTTP
        errorMessage = response.statusText || errorMessage;
      }

      throw new Error(errorMessage);
    }
    return response; // Continua la catena .then se la risposta è ok
  });
}

/**
 * Funzione semplificata per richieste GET API
 * @param url - URL relativo dell'API
 * @param options - Opzioni aggiuntive per fetch
 * @returns Promise con dati JSON
 */
export async function apiGet<T = any>(url: string, options: FetchOptions = {}): Promise<T> {
  const response = await apiFetch(url, {
    method: 'GET',
    ...options
  });

  // Il controllo response.ok viene gestito all'interno di apiFetch
  return await response.json();
}

/**
 * Funzione semplificata per richieste POST API
 * @param url - URL relativo dell'API
 * @param data - Dati da inviare come corpo della richiesta
 * @param options - Opzioni aggiuntive per fetch
 * @returns Promise con dati JSON
 */
export async function apiPost<T = any>(
  url: string, 
  data: any, 
  options: FetchOptions = {}
): Promise<T> {
  const { headers = {}, ...restOptions } = options;

  // Creazione header personalizzati
  const customHeaders: Record<string, string> = { 'Content-Type': 'application/json' };

  // Aggiungi gli header personalizzati
  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value === 'string') {
        customHeaders[key] = value;
      }
    }
  }

  const response = await apiFetch(url, {
    method: 'POST',
    headers: customHeaders,
    body: JSON.stringify(data),
    ...restOptions
  });

  // Il controllo response.ok viene gestito all'interno di apiFetch
  return await response.json();
}

/**
 * Funzione semplificata per richieste PUT API
 * @param url - URL relativo dell'API
 * @param data - Dati da inviare come corpo della richiesta
 * @param options - Opzioni aggiuntive per fetch
 * @returns Promise con dati JSON
 */
export async function apiPut<T = any>(
  url: string, 
  data: any, 
  options: FetchOptions = {}
): Promise<T> {
  const { headers = {}, ...restOptions } = options;

  // Creazione header personalizzati
  const customHeaders: Record<string, string> = { 'Content-Type': 'application/json' };

  // Aggiungi gli header personalizzati
  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value === 'string') {
        customHeaders[key] = value;
      }
    }
  }

  const response = await apiFetch(url, {
    method: 'PUT',
    headers: customHeaders,
    body: JSON.stringify(data),
    ...restOptions
  });

  // Il controllo response.ok viene gestito all'interno di apiFetch
  return await response.json();
}

/**
 * Funzione semplificata per richieste DELETE API
 * @param url - URL relativo dell'API
 * @param options - Opzioni aggiuntive per fetch
 * @returns Promise con dati JSON
 */
export async function apiDelete<T = any>(url: string, options: FetchOptions = {}): Promise<T> {
  const response = await apiFetch(url, {
    method: 'DELETE',
    ...options
  });

  // Il controllo response.ok viene gestito all'interno di apiFetch
  return await response.json();
}

/**
 * Funzione per ottenere gli headers di autenticazione
 * @returns Headers con token Bearer se disponibile
 */
export const getAuthHeaders = () => {
  const token = getToken();
  console.log('[API HELPER] Token ottenuto in getAuthHeaders:', !!token);

  if (!token) {
    console.warn('[API HELPER] ⚠️ Nessun token disponibile per l\'autenticazione');
    return {};
  }

  // Assicurati che il token sia una stringa valida
  if (typeof token !== 'string' || token.length < 10) {
    console.warn('[API HELPER] ⚠️ Token non valido (formato):', typeof token, token?.length);
    return {};
  }

  console.log('[API HELPER] ✅ Token valido recuperato per headers');
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Interfaccia generale per interazioni API
 */
export const apiService = {
  fetch: apiFetch,
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  normalizeUrl: normalizeApiUrl,
  getAuthHeaders
};

export default apiService;